import { useEffect } from 'react';
import downloadFileWithSrc from '~/components/bad/HTMLEditor/util/downloadFileWithSrc';
import uploadImageToStorage from '~/components/bad/HTMLEditor/util/uploadImageToStorage';
import useAddToast from '~/hooks/useAddToast';

import { isNil } from 'ramda';
import type {
  ClientFlowActionSendEmailPlain,
  ClientFlowActionSendNotification,
  EmailFileAttachment,
} from '~/graphql/types.client';
import formatToastMessage from '~/util/formatToastMessage';
import { useRecoilState, useSetRecoilState } from 'recoil';
import interactions, {
  type InteractionCopyAction,
} from '~/components/page/Automation/v2/state/interactions';
import flowActions from '~/components/page/Automation/v2/state';
import type { NodeData } from '~/components/page/Automation/v2/components/Builder/components/nodeTypes/components/CardTemplate';
import useCurrentUser from '~/hooks/useCurrentUser';
import useErrorReporter from '~/hooks/useErrorReporter';

const text = {
  inlineImageUploadError:
    'Afbeelding kon niet worden geüpload bij het dupliceren van de actie. Controleer de afbeeldingen in de nieuwe actie "E-mail versturen" en plaats ze opnieuw.',
  fileUploadError:
    'Bijlage kon niet worden geüpload bij het dupliceren van de actie. Controleer de bijlagen in de nieuwe actie "E-mail versturen" en upload ze opnieuw.',
};

/**
 * We need to update the file in the Flow___Argument_File attachments when the action is duplicated
 * because files cannot be reused in different actions
 */
const useReuploadAttachments = ({
  data,
}: {
  data: NodeData<
    ClientFlowActionSendNotification | ClientFlowActionSendEmailPlain
  >;
}): { hasFilesToUpload: boolean } => {
  const user = useCurrentUser();
  const addToast = useAddToast();
  const errorReporter = useErrorReporter();

  const [activeInteraction, setActiveInteraction] =
    useRecoilState(interactions);
  const setActions = useSetRecoilState(flowActions);

  const fileAttachments = data.action.attachments.filter(
    a => a.file.__typename === 'Flow___Argument_File',
  );

  const isDuplicatedAction =
    !isNil(activeInteraction) &&
    activeInteraction.type === 'duplicateAction' &&
    activeInteraction.action.id === data.action.id;

  const isCopiedAction =
    !isNil(activeInteraction) &&
    activeInteraction.type === 'copyAction' &&
    activeInteraction.pastedActionId === data.action.id;

  const hasFilesToUpload =
    (isDuplicatedAction || isCopiedAction) && fileAttachments.length > 0;

  useEffect(() => {
    const attachments = data.action.attachments;
    const pointerAttachments = attachments.filter(
      a => a.file.__typename === 'Flow___Argument_Pointer',
    );

    const reuploadFiles = async () => {
      const newFileAttachments: Array<EmailFileAttachment> = [];

      // Reupload each file separately. If it fails, add it as a broken file attachment
      for (const attachment of fileAttachments) {
        if (attachment.file.__typename === 'Flow___Argument_Pointer') continue;

        const copiedFile = attachment.file.value_file;

        if (!attachment.url) continue;

        try {
          await downloadFileWithSrc(attachment.url, async file => {
            const res = await uploadImageToStorage(file, user.id);

            if (res) {
              newFileAttachments.push({
                __typename: 'FlowV2_EmailAttachment',
                inlineId: attachment.inlineId || null,
                url: res.url,
                file: {
                  __typename: 'Flow___Argument_File',
                  value_file: {
                    __typename: 'Flow___File',
                    s3key: res.s3key,
                    filename: copiedFile.filename,
                    contentType: res.contentType,
                    contentLength: res.contentLength,
                  },
                },
              });
            }
          });
        } catch {
          // Add a broken file attachment to be able to display the file in the SendNotification action modal.
          // We do not add the ones for inline images here, they are displayed inside the DHEditor
          if (isNil(attachment.inlineId)) {
            newFileAttachments.push({
              __typename: 'FlowV2_EmailAttachment',
              inlineId: null,
              url: '',
              file: {
                __typename: 'Flow___Argument_File',
                value_file: {
                  filename: copiedFile.filename,
                  __typename: 'Flow___File',
                  s3key: '',
                  contentType: '',
                  contentLength: 0,
                },
              },
            });

            addToast([formatToastMessage(text.fileUploadError, 'danger')]);
          } else {
            addToast([
              formatToastMessage(text.inlineImageUploadError, 'danger'),
            ]);
          }

          errorReporter.captureException(
            new Error(
              `FileUploadError when duplicating SendNotification action with file attachments ${JSON.stringify(
                attachment,
              )}`,
            ),
            'warning',
          );
        }
      }

      // If newFileAttachments haven't been uploaded because of an error, pass them as empty array anyway.
      // We want to remove them from the existing attachments so that the user can manually fix the
      // file attachments in the faulty action.
      const updatedAttachments = [...pointerAttachments, ...newFileAttachments];

      if (updatedAttachments.length > 0) {
        setActions(prev =>
          [...prev].map(a => {
            if (a.id === data.action.id)
              return { ...a, attachments: updatedAttachments };

            return a;
          }),
        );
      }

      if (activeInteraction?.type === 'duplicateAction')
        setActiveInteraction(null);

      if (activeInteraction?.type === 'copyAction') {
        // reset it after we are done with uploading
        setActiveInteraction((prev: InteractionCopyAction) => ({
          ...prev,
          pastedActionId: null,
        }));
      }
    };

    if (hasFilesToUpload) void reuploadFiles();

    // Do not add data.action.attachments, it causes unnecessary calls to uploadImageToStorage functions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeInteraction,
    data.action.id,
    setActions,
    setActiveInteraction,
    user.id,
    hasFilesToUpload,
  ]);

  return { hasFilesToUpload };
};

export default useReuploadAttachments;
